import React, { useEffect, useState } from 'react';
import { X, CheckCircle, AlertCircle } from 'lucide-react';
import { auditAPI } from '../../../api/audit';

const ACTION_COLOR = {
  CREATE:  'bg-green-100 text-green-700',
  UPDATE:  'bg-blue-100 text-blue-700',
  DELETE:  'bg-red-100 text-red-700',
  LOGIN:   'bg-purple-100 text-purple-700',
  LOGOUT:  'bg-gray-100 text-gray-700',
  APPROVE: 'bg-emerald-100 text-emerald-700',
  REJECT:  'bg-orange-100 text-orange-700',
  PROCESS: 'bg-indigo-100 text-indigo-700',
  EXPORT:  'bg-yellow-100 text-yellow-700',
  ACCESS:  'bg-slate-100 text-slate-700',
};

const fmtDate = (d) => d ? new Date(d).toLocaleString('es-DO', {
  year: 'numeric', month: 'short', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit'
}) : '—';

// Campos técnicos que no aportan al diff y se omiten
const SKIP_FIELDS = new Set([
  'updated_at', 'created_at', 'deleted_at',
  'updated_by', 'created_by', 'password_hash',
]);

// Detecta si un string parece fecha ISO
const ISO_RE  = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
// Detecta UUID v4
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Convierte un valor a texto legible para la tabla de diff */
function fmtVal(val) {
  if (val === null || val === undefined) return <span className="text-gray-300 italic">—</span>;
  if (typeof val === 'boolean') return val ? 'Sí' : 'No';
  if (typeof val === 'number')  return val.toLocaleString('es-DO');
  if (Array.isArray(val)) {
    if (val.length === 0) return <span className="text-gray-300 italic">—</span>;
    return val.join(', ');
  }
  if (typeof val === 'string') {
    if (ISO_RE.test(val))  return fmtDate(val);
    if (UUID_RE.test(val)) return (
      <span className="font-mono text-[10px] text-gray-400" title={val}>
        {val.slice(0, 8)}…
      </span>
    );
    if (/^-?\d+(\.\d+)?$/.test(val)) return parseFloat(val).toLocaleString('es-DO');
    return val;
  }
  return <span className="font-mono text-[10px] text-gray-500">{JSON.stringify(val)}</span>;
}

/** Compara dos objetos y devuelve solo los campos que cambiaron */
function diffObjects(before, after) {
  const parse = (d) => {
    if (!d) return {};
    try { return typeof d === 'string' ? JSON.parse(d) : d; }
    catch { return {}; }
  };

  const b = parse(before);
  const a = parse(after);
  const allKeys = new Set([...Object.keys(b), ...Object.keys(a)]);
  const changes = [];

  for (const key of allKeys) {
    if (SKIP_FIELDS.has(key)) continue;
    const bVal = b[key];
    const aVal = a[key];
    if (JSON.stringify(bVal) !== JSON.stringify(aVal)) {
      changes.push({ key, before: bVal, after: aVal });
    }
  }
  return changes;
}

/** Tabla de diff: campo | antes | después */
function DiffTable({ before, after, action }) {
  const hasBefore = !!before;
  const hasAfter  = !!after;

  if (!hasBefore && !hasAfter) return null;

  // CREATE: solo after — mostrar campos nuevos
  if (!hasBefore && hasAfter) {
    const parse = (d) => { try { return typeof d === 'string' ? JSON.parse(d) : d; } catch { return {}; } };
    const entries = Object.entries(parse(after));
    return (
      <div className="rounded-lg border border-green-200 overflow-hidden">
        <div className="bg-green-50 px-3 py-2 border-b border-green-200">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-green-700">Registro creado</span>
        </div>
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 text-gray-400 text-[10px] uppercase tracking-wider">
              <th className="text-left px-3 py-2 w-1/3">Campo</th>
              <th className="text-left px-3 py-2">Valor</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {entries.map(([key, val]) => (
              <tr key={key} className="bg-green-50/40">
                <td className="px-3 py-1.5 text-gray-500 font-medium">{key}</td>
                <td className="px-3 py-1.5 text-green-800">{fmtVal(val)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // DELETE: solo before — mostrar campos eliminados
  if (hasBefore && !hasAfter) {
    const parse = (d) => { try { return typeof d === 'string' ? JSON.parse(d) : d; } catch { return {}; } };
    const entries = Object.entries(parse(before));
    return (
      <div className="rounded-lg border border-red-200 overflow-hidden">
        <div className="bg-red-50 px-3 py-2 border-b border-red-200">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-red-700">Registro eliminado</span>
        </div>
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 text-gray-400 text-[10px] uppercase tracking-wider">
              <th className="text-left px-3 py-2 w-1/3">Campo</th>
              <th className="text-left px-3 py-2">Valor</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {entries.map(([key, val]) => (
              <tr key={key} className="bg-red-50/40">
                <td className="px-3 py-1.5 text-gray-500 font-medium">{key}</td>
                <td className="px-3 py-1.5 text-red-800 line-through decoration-red-300">{fmtVal(val)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // UPDATE / PROCESS: mostrar solo campos que cambiaron
  const changes = diffObjects(before, after);

  if (changes.length === 0) {
    return (
      <div className="text-xs text-gray-400 italic text-center py-3">
        Sin cambios detectados en los datos
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Cambios realizados</span>
        <span className="text-[10px] text-gray-400">{changes.length} campo{changes.length !== 1 ? 's' : ''} modificado{changes.length !== 1 ? 's' : ''}</span>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-gray-50 text-gray-400 text-[10px] uppercase tracking-wider border-b border-gray-100">
            <th className="text-left px-3 py-2 w-1/3">Campo</th>
            <th className="text-left px-3 py-2 w-1/3 border-l border-gray-100">Antes</th>
            <th className="text-left px-3 py-2 w-1/3 border-l border-gray-100">Después</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {changes.map(({ key, before: bVal, after: aVal }) => (
            <tr key={key} className="hover:bg-blue-50/30 transition-colors">
              <td className="px-3 py-2 text-gray-500 font-medium">{key}</td>
              <td className="px-3 py-2 text-red-600 bg-red-50/30 border-l border-gray-100">{fmtVal(bVal)}</td>
              <td className="px-3 py-2 text-green-700 bg-green-50/30 border-l border-gray-100">{fmtVal(aVal)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Row({ label, value, mono }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex gap-3 py-1.5 border-b border-gray-50 last:border-0">
      <span className="text-xs text-gray-400 w-28 shrink-0">{label}</span>
      <span className={`text-xs text-gray-800 break-all ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}

export default function AuditDetail({ logId, onClose }) {
  const [log, setLog]         = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!logId) return;
    setLoading(true);
    auditAPI.getById(logId)
      .then(setLog)
      .finally(() => setLoading(false));
  }, [logId]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-xl">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-bold text-gray-900">Detalle del Evento</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : !log ? (
          <div className="flex-1 flex items-center justify-center py-16 text-sm text-gray-400">
            Registro no encontrado
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-5 space-y-5">

            {/* Action badge + status */}
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${ACTION_COLOR[log.action] || 'bg-gray-100 text-gray-700'}`}>
                {log.action}
              </span>
              {log.status === 'success'
                ? <span className="flex items-center gap-1 text-xs text-green-600"><CheckCircle className="w-3.5 h-3.5" /> Éxito</span>
                : <span className="flex items-center gap-1 text-xs text-red-600"><AlertCircle className="w-3.5 h-3.5" /> Error</span>
              }
              <span className="text-xs text-gray-400 ml-auto">ID #{log.id}</span>
            </div>

            {/* Descripción */}
            {log.description && (
              <p className="text-sm text-gray-700 bg-gray-50 rounded-lg px-4 py-3">{log.description}</p>
            )}

            {/* Info grid */}
            <div className="bg-white rounded-lg border border-gray-100 px-4 py-2">
              <Row label="Usuario"     value={log.user_name} />
              <Row label="Rol"         value={log.user_role} />
              <Row label="Módulo"      value={log.module} />
              <Row label="Entidad"     value={log.entity} />
              <Row label="ID Registro" value={log.entity_id} mono />
              <Row label="Fecha"       value={fmtDate(log.created_at)} />
              <Row label="IP"          value={log.ip_address === '::1' ? 'localhost' : log.ip_address} mono />
              <Row label="Dispositivo" value={log.user_agent} />
              {log.error_details && <Row label="Error" value={log.error_details} />}
            </div>

            {/* Diff de cambios */}
            {(log.before_data || log.after_data) && (
              <DiffTable
                before={log.before_data}
                after={log.after_data}
                action={log.action}
              />
            )}

            {/* Metadata */}
            {log.metadata && (() => {
              const meta = typeof log.metadata === 'string' ? JSON.parse(log.metadata) : log.metadata;
              const entries = Object.entries(meta);
              if (!entries.length) return null;
              return (
                <div className="rounded-lg border border-gray-200 overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Metadata</span>
                  </div>
                  <table className="w-full text-xs">
                    <tbody className="divide-y divide-gray-50">
                      {entries.map(([key, val]) => (
                        <tr key={key}>
                          <td className="px-3 py-1.5 text-gray-400 font-medium w-1/3">{key}</td>
                          <td className="px-3 py-1.5 text-gray-700">{fmtVal(val)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}

          </div>
        )}
      </div>
    </div>
  );
}
